import { useCallback } from 'react'
import { useAnalyticsTrack } from '@/features/amplitude'
import type { MessageScope, MessageTab } from '../lib/types'
import { ADVISER_EVENTS } from './adviser-analytics.constants'

interface SourceClickedProps {
  articleId: string | null
  articleUrl: string
  position: number
}

interface ChatFailedProps {
  conversationId: number | null
  phase: 'send' | 'stream'
  errorName: string
}

interface LaunchProps {
  launchContext: string | null
  entryPoint: string | null
}

const launchTrackProps = (launch?: LaunchProps) =>
  launch?.launchContext
    ? { launchContext: launch.launchContext, entryPoint: launch.entryPoint }
    : undefined

export const useAdviserAnalytics = () => {
  const track = useAnalyticsTrack()

  const trackWidgetDisplayed = useCallback(
    () => track(ADVISER_EVENTS.WidgetDisplayed),
    [track]
  )

  const trackGreetingDismissed = useCallback(
    () => track(ADVISER_EVENTS.GreetingDismissed),
    [track]
  )

  const trackWidgetViewed = useCallback(
    (launch?: LaunchProps) => {
      const launchProps = launchTrackProps(launch)
      return launchProps
        ? track(ADVISER_EVENTS.WidgetViewed, launchProps)
        : track(ADVISER_EVENTS.WidgetViewed)
    },
    [track]
  )

  const trackWidgetClosed = useCallback(
    () => track(ADVISER_EVENTS.WidgetClosed),
    [track]
  )

  const trackEntryPointClicked = useCallback(
    (launch: LaunchProps) =>
      track(ADVISER_EVENTS.EntryPointClicked, {
        launchContext: launch.launchContext,
        entryPoint: launch.entryPoint,
      }),
    [track]
  )

  const trackConversationStarted = useCallback(
    (conversationId: number, launch?: LaunchProps) =>
      track(ADVISER_EVENTS.ConversationStarted, {
        conversationId,
        ...launchTrackProps(launch),
      }),
    [track]
  )

  const trackConversationResumed = useCallback(
    (conversationId: number, launch?: LaunchProps) =>
      track(ADVISER_EVENTS.ConversationResumed, {
        conversationId,
        ...launchTrackProps(launch),
      }),
    [track]
  )

  const trackNextStepClicked = useCallback(
    (destination: string) =>
      track(ADVISER_EVENTS.NextStepClicked, { destination }),
    [track]
  )

  const trackSourceClicked = useCallback(
    (props: SourceClickedProps) =>
      track(ADVISER_EVENTS.SourceClicked, {
        articleId: props.articleId,
        articleUrl: props.articleUrl,
        position: props.position,
      }),
    [track]
  )

  const trackHistoryOpened = useCallback(
    () => track(ADVISER_EVENTS.HistoryOpened),
    [track]
  )

  const trackNewConversationClicked = useCallback(
    () => track(ADVISER_EVENTS.NewConversationClicked),
    [track]
  )

  const trackDeleteConversationClicked = useCallback(
    (conversationId: number) =>
      track(ADVISER_EVENTS.DeleteConversationClicked, { conversationId }),
    [track]
  )

  const trackChatFailed = useCallback(
    (props: ChatFailedProps) =>
      track(ADVISER_EVENTS.ChatFailed, {
        conversationId: props.conversationId,
        phase: props.phase,
        errorName: props.errorName,
      }),
    [track]
  )

  const trackMessagesListViewed = useCallback(
    (props: { tab: MessageTab; scope: MessageScope }) =>
      track(ADVISER_EVENTS.MessagesListViewed, props),
    [track]
  )

  const trackMessageThreadOpened = useCallback(
    (ticketId: number) =>
      track(ADVISER_EVENTS.MessageThreadOpened, { ticketId }),
    [track]
  )

  const trackMessageReplySent = useCallback(
    (ticketId: number) => track(ADVISER_EVENTS.MessageReplySent, { ticketId }),
    [track]
  )

  const trackAttachmentAdded = useCallback(
    (props: { size: number; contentType: string }) =>
      track(ADVISER_EVENTS.AttachmentAdded, {
        size: props.size,
        contentType: props.contentType,
      }),
    [track]
  )

  const trackAttachmentUploadFailed = useCallback(
    (props: { reason: string; percent: number }) =>
      track(ADVISER_EVENTS.AttachmentUploadFailed, {
        reason: props.reason,
        percent: props.percent,
      }),
    [track]
  )

  const trackAttachmentRemoved = useCallback(
    () => track(ADVISER_EVENTS.AttachmentRemoved),
    [track]
  )

  const trackAttachmentsSubmitted = useCallback(
    (props: { count: number }) =>
      track(ADVISER_EVENTS.AttachmentsSubmitted, { count: props.count }),
    [track]
  )

  return {
    trackWidgetDisplayed,
    trackGreetingDismissed,
    trackWidgetViewed,
    trackWidgetClosed,
    trackEntryPointClicked,
    trackConversationStarted,
    trackConversationResumed,
    trackSourceClicked,
    trackNextStepClicked,
    trackHistoryOpened,
    trackNewConversationClicked,
    trackDeleteConversationClicked,
    trackChatFailed,
    trackMessagesListViewed,
    trackMessageThreadOpened,
    trackMessageReplySent,
    trackAttachmentAdded,
    trackAttachmentUploadFailed,
    trackAttachmentRemoved,
    trackAttachmentsSubmitted,
  }
}
